/**
 * Typed fetch helper. Every call goes to /api/v1 (proxied to the Workers dev
 * server in dev), sends the session cookie, and throws the server's error
 * shape { error, message, details } so screens can render it in words.
 */
import { API_BASE, type ApiError } from "@crc/shared";

/** Same-origin by default (the dev proxy and a Pages Function both serve /api); set VITE_API_BASE to the Worker's origin when the API is deployed on its own host. */
const BASE = `${((import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE ?? "").replace(/\/$/, "")}${API_BASE}`;

export class ApiRequestError extends Error implements ApiError {
  constructor(
    public status: number,
    public error: string,
    message?: string,
    public details?: unknown,
  ) {
    super(message ?? error);
  }
}

export async function apiFetch<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...rest,
    headers: { ...(json !== undefined ? { "content-type": "application/json" } : {}), ...(headers as Record<string, string>) },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const e = (data ?? {}) as Partial<ApiError>;
    throw new ApiRequestError(res.status, e.error ?? `http_${res.status}`, e.message ?? (res.status === 401 ? "Sign in to continue" : `Request failed (${res.status})`), e.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, json?: unknown, init?: Pick<RequestInit, "signal">) => apiFetch<T>(path, { method: "POST", json, ...init }),
  patch: <T>(path: string, json?: unknown) => apiFetch<T>(path, { method: "PATCH", json }),
  put: <T>(path: string, json?: unknown) => apiFetch<T>(path, { method: "PUT", json }),
  del: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => apiFetch<T>(path, { method: "POST", body: form }),
};

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "" && v !== false) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}
